import React, { useEffect, useState, memo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import PaymentMethods from "./PaymentMethods";
import NewPaymentForm from "./NewPaymentForm";
import OrderSummary from "./OrderSummary";
import "./PaymentForm.scss";
import { getSubscription } from "../../../utils/subscriptions";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUserInformation } from "../../../store/userRole";
import Loader from "../../../components/Loader";
import { toast } from "react-toastify";
import FaArrowLeft from "../../../constants/icons/Fa/FaArrowLeft";

const PaymentPage = () => {
  const [subscriptionsData, setSubscriptionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const userInformation = useSelector(getUserInformation);
  const formikRef = { current: null };
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedOption, planDetails, customHours } = location.state || {};
  const { id: classroomId } = useParams();

  // State for selected pricing options, keyed by subscription._id
  const [selectedPricingOptions, setSelectedPricingOptions] = useState({});
  const [initialValues, setInitialValues] = useState({ subscriptions: {} });

  // State for selected bank (HBL or ALFA)
  const [selectedBank, setSelectedBank] = useState("HBL");

  // Fetch subscription data
  const fetchSubscriptionsData = async (subscriptionIds) => {
    try {
      const dataPromises = subscriptionIds.map((subscriptionId) =>
        getSubscription(subscriptionId),
      );
      const dataResponses = await Promise.all(dataPromises);
      const enrichedData = dataResponses.map((sub) => ({
        ...sub,
        applicableOptions:
          planDetails?._id === sub._id
            ? planDetails.applicableOptions
            : sub.pricingOptions || [],
      }));
      setSubscriptionsData(enrichedData);
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
    }
  };

  const handleGoBack = () => {
    navigate(`/student/myClassrooms/${classroomId}/pricing`);
  };

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const subscriptionIds = query.get("subscriptionId");

    if (subscriptionIds) {
      const subscriptionIdList = subscriptionIds.split(",");
      setLoading(true);
      fetchSubscriptionsData(subscriptionIdList).then(() => setLoading(false));
    }

    // Load saved form data if available
    const savedFormData = localStorage.getItem("paymentFormData");
    if (savedFormData) {
      setInitialValues(JSON.parse(savedFormData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Initialize selected pricing options
  useEffect(() => {
    if (subscriptionsData.length > 0) {
      const initialOptions = {};
      subscriptionsData.forEach((subscription) => {
        if (
          planDetails &&
          subscription._id === planDetails._id &&
          selectedOption
        ) {
          // For hourly plans, ensure we're using the custom hours data
          if (selectedOption.pricingModel === "Hourly Plan" && customHours) {
            // console.log("Hourly Plan detected with custom hours:", customHours);
            // console.log("Selected option:", selectedOption);
          }
          initialOptions[subscription._id] = selectedOption;
        } else {
          initialOptions[subscription._id] = subscription.applicableOptions[0];
        }
      });
      setSelectedPricingOptions(initialOptions);
      setInitialValues({ subscriptions: initialOptions });

      // Set the selected bank based on the first subscription's payment gateway
      if (subscriptionsData[0]?.paymentGateway) {
        setSelectedBank(subscriptionsData[0].paymentGateway);
      }
    }
  }, [subscriptionsData, planDetails, selectedOption, customHours]);

  const handleCompletePayment = async () => {
    setLoading(true);
    if (formikRef.current) {
      const errors = await formikRef.current.validateForm();
      if (Object.keys(errors).length > 0) {
        toast.error("Please fill out all required fields.");
        setLoading(false);
        return;
      }
      formikRef.current.handleSubmit();
    } else {
      console.error("Formik ref is null");
      setLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  // Calculate total amount based on selected pricing options
  const totalAmount = subscriptionsData.reduce((acc, subscription) => {
    const option =
      selectedPricingOptions[subscription._id] ||
      subscription.applicableOptions[0];
    if (!option) return acc;
    if (option.pricingModel === "Hourly Plan") {
      const pricePerHour = parseFloat(option.perHourPrice || 0);
      const hours = parseFloat(option.hours || 0);
      const discount = parseFloat(option.discount || 0);
      return acc + pricePerHour * hours * (1 - discount / 100);
    }
    return (
      acc +
      (option.finalPrice || option.price * (1 - (option.discount || 0) / 100))
    );
  }, 0);

  return (
    <Container>
      <Row className="mt-3 mb-2">
        <Col>
          <Button
            variant="outline-primary"
            className="d-flex align-items-center"
            onClick={handleGoBack}
          >
            <FaArrowLeft className="me-2" /> Back to Packages
          </Button>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col md={7} className="mb-3">
          <PaymentMethods
            selectedBank={selectedBank}
            setSelectedBank={setSelectedBank}
          />
          <NewPaymentForm
            subscriptionData={subscriptionsData}
            classroomId={classroomId}
            formikRef={formikRef}
            userInformation={userInformation}
            initialValues={initialValues}
            setLoading={setLoading}
            selectedPricingOptions={selectedPricingOptions}
            setSelectedPricingOptions={setSelectedPricingOptions}
            selectedBank={selectedBank}
          />
        </Col>
        <Col md={5}>
          <OrderSummary
            subscriptionsData={subscriptionsData}
            totalAmount={totalAmount}
            onCompletePayment={handleCompletePayment}
            loading={loading}
            selectedPricingOptions={selectedPricingOptions}
            selectedBank={selectedBank}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default memo(PaymentPage);
